// src/formErrors.js
//
// WHAT THIS FILE IS:
// A small shared helper used by every Form component. When Django
// rejects a save (e.g. a required field was left empty, or an email
// was invalid), DRF responds with a JSON object shaped like:
//   { "email": ["This field is required."], "name": ["This field may not be blank."] }
//
// Without this helper, every form was just doing
// setError(JSON.stringify(errorData)) - which shows the user a raw,
// ugly wall of JSON. This function turns that into a clean, readable
// sentence instead, e.g.:
//   "email: This field is required. | name: This field may not be blank."
//
// Putting this logic in ONE shared file means every form behaves the
// same way, and if we ever want to improve error formatting again, we
// only have to change it here - not in nine different files.

export function formatApiError(errorData) {
  // If Django/DRF gives us a single top-level message (some errors come
  // back as { "detail": "..." } instead of per-field errors), just use that directly.
  if (errorData && typeof errorData.detail === 'string') {
    return errorData.detail;
  }

  // Otherwise, assume it's the per-field shape and build a readable
  // string out of it. Object.entries() turns { field: [...] } into an
  // array of [field, messages] pairs we can loop over.
  if (errorData && typeof errorData === 'object') {
    const parts = Object.entries(errorData).map(([field, messages]) => {
      // messages is usually an array of strings - join them with a
      // space in case a field has more than one error at once.
      const text = Array.isArray(messages) ? messages.join(' ') : String(messages);
      return `${field}: ${text}`;
    });
    if (parts.length > 0) {
      return parts.join(' | ');
    }
  }

  // Fallback for anything unexpected, so the user never sees a blank error.
  return 'Something went wrong while saving. Please check your entries and try again.';
}
